import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'antd/lib/button';
import ColorPicker from 'antd/lib/color-picker';
import Empty from 'antd/lib/empty';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Spin from 'antd/lib/spin';
import Text from 'antd/lib/typography/Text';
import {
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    PlusOutlined,
    SortAscendingOutlined,
    TagsOutlined,
} from '@ant-design/icons';
import { Content } from 'antd/lib/layout/layout';
import { tryFetchEntity } from '@/actions/entities';
import { OrgUpdatable } from '@/actions/organization';
import { CreateOrgTagRequest, UpdateOrgTagRequest } from '@/actions/orgSettings';
import * as API from '@/api';
import { FetchableEntityType } from '@/entityType';
import { displayStringForDestination } from '@/helpers/displayHelpers';
import { useMdmPlatformOverride, useMdmPlatformOverrideResolver } from '@/helpers/mdmPlatformOverride';
import { RootState } from '@/reducers/reducers';
import { selectAuthCredentials, selectOrg } from '@/selectors/selectors';
import { selectAvailableDestinations, selectDestinationsData } from '@/selectors/summaryViewSelectors';
import { OrgTag, RunwayResponseType } from '@/types';
import { Destination } from '@/types/destinations';
import { useCallUpdatable, useIsLoading } from '@/utils/hooks/helpers';
import { globalMessage } from '@/utils/globalMessage';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { DestinationIcon } from '../sidebar/shared/components';

interface OrgTagsSettingsProps {
    orgId: string;
}

interface OrgTagDraft {
    id?: string;
    tagName: string;
    color: string;
    destinations: string[];
}

const tagColorsPresets = ['#1890FF', '#FA8C16', '#52C41A', '#722ED1', '#EB2F96', '#08979C', '#613400']; // these are the same colors that we use on the release calendar

const DEFAULT_TAG_COLOR = tagColorsPresets[0];

const tagColorPresets = [
    {
        label: 'Recommended',
        colors: tagColorsPresets,
    },
];

const tagRowStyle: React.CSSProperties = {
    border: `1px solid ${antGrey.grey4}`,
    borderRadius: 8,
    padding: '20px 20px 20px 48px',
    backgroundColor: runwayColors.white,
};

const fieldRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
};

const formItemStyle: React.CSSProperties = {
    marginBottom: 0,
};

const formItemLabelCol = { flex: '90px' };
const destinationsFormItemLabelCol = { flex: '110px' };
const destinationsSelectWidth = 560;
const destinationsFormItemWidth = 680;

const colorFormItemStyle: React.CSSProperties = {
    ...formItemStyle,
    marginLeft: 16,
};

const actionsRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
};

const fallbackDestinationIconStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    borderRadius: 4,
    backgroundColor: antGrey.grey6,
    color: runwayColors.white,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
};

const toDraftTag = (tag: OrgTag): OrgTagDraft => ({
    id: tag.id,
    tagName: tag.tagName,
    color: tag.color || DEFAULT_TAG_COLOR,
    destinations: tag.destinations ?? [],
});

const sortDraftTags = (tags: OrgTagDraft[]) =>
    [...tags].sort((a, b) => {
        if (a.id && !b.id) return 1;
        if (!a.id && b.id) return -1;
        return a.tagName.localeCompare(b.tagName);
    });

const tagPayload = (tag: OrgTagDraft): CreateOrgTagRequest => ({
    tagName: tag.tagName.trim(),
    color: tag.color,
    destinations: tag.destinations,
});

const hasRequiredTagFields = (tag: OrgTagDraft) =>
    tag.tagName.trim().length > 0 && tag.color.trim().length > 0 && tag.destinations.length > 0;

const destinationDisplayName = (destination: Destination, mdmPlatformLabel?: string) =>
    displayStringForDestination(
        destination.platform,
        destination.appStoreType,
        destination.name,
        destination.appName,
        mdmPlatformLabel,
        destination.shouldOmitPlatformFromAppName,
    );

const destinationValue = (destination: Destination) => destination.destinationId ?? destination.stepConfigId;

const colorPickerValue = (color: string) => {
    if (!color || color.includes('var(')) {
        return DEFAULT_TAG_COLOR;
    }

    return color;
};

const DestinationChip: React.FC<{
    destinationId: string;
    destination?: Destination;
    onClose: (event?: React.MouseEvent<HTMLElement>) => void;
}> = ({ destinationId, destination, onClose }) => {
    const mdmPlatformLabel = useMdmPlatformOverride(destination?.appId)?.label;
    const name = destination ? destinationDisplayName(destination, mdmPlatformLabel) : destinationId;

    return (
        <span
            onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
            }}
            style={{
                height: 24,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '0 8px',
                borderRadius: 4,
                backgroundColor: antGrey.grey2,
                color: runwayColors.darkTextGrey,
                opacity: 1,
            }}
        >
            {destination ?
                <DestinationIcon destination={destination} size={16} />
            :   <span style={fallbackDestinationIconStyle}>
                    <TagsOutlined style={{ fontSize: 10 }} />
                </span>
            }
            <Text style={{ color: runwayColors.darkTextGrey }}>{name}</Text>
            <CloseOutlined
                style={{ color: antGrey.grey6, fontSize: 11, cursor: 'pointer' }}
                onClick={(event) => {
                    event.stopPropagation();
                    onClose(event);
                }}
                aria-label={`Remove ${name}`}
            />
        </span>
    );
};

const OrgTagsSettings: React.FC<OrgTagsSettingsProps> = ({ orgId }) => {
    const dispatch = useDispatch();
    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));
    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const orgDestinationsData = useSelector((state: RootState) => selectDestinationsData(state, orgId));
    const availableDestinations = useSelector((state: RootState) => selectAvailableDestinations(state, orgId));
    const isLoadingOrgDestinations = useIsLoading(FetchableEntityType.OrgDestinationsAndVersions, orgId);

    const resolveMdmOverride = useMdmPlatformOverrideResolver();

    const [tags, setTags] = React.useState<OrgTagDraft[]>([]);
    const [savedTags, setSavedTags] = React.useState<OrgTagDraft[]>([]);
    const [isFetchingTags, setIsFetchingTags] = React.useState(false);

    const [createOrgTag, createOrgTagIsLoading, createOrgTagResponseType, createOrgTagError] = useCallUpdatable(
        OrgUpdatable.CreateOrgTag,
        { orgId },
    );
    const [updateOrgTag, updateOrgTagIsLoading, updateOrgTagResponseType, updateOrgTagError] = useCallUpdatable(
        OrgUpdatable.UpdateOrgTag,
        { orgId },
    );
    const [deleteOrgTag, deleteOrgTagIsLoading, deleteOrgTagResponseType, deleteOrgTagError] = useCallUpdatable(
        OrgUpdatable.DeleteOrgTag,
        { orgId },
    );

    const destinationById = React.useMemo(() => {
        return availableDestinations.reduce<Record<string, Destination>>((acc, destination) => {
            acc[destination.stepConfigId] = destination;
            if (destination.destinationId) {
                acc[destination.destinationId] = destination;
            }
            return acc;
        }, {});
    }, [availableDestinations]);

    const destinationOptions = React.useMemo(() => {
        return availableDestinations
            .map((destination) => ({
                destination,
                label: destinationDisplayName(destination, resolveMdmOverride(destination.appId)?.label),
                value: destinationValue(destination),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [availableDestinations, resolveMdmOverride]);

    const destinationTagRender = (props: {
        value: string | number;
        onClose: (event?: React.MouseEvent<HTMLElement>) => void;
    }) => {
        const destinationId = String(props.value);

        return (
            <DestinationChip
                destinationId={destinationId}
                destination={destinationById[destinationId]}
                onClose={(event) => {
                    props.onClose(event);
                }}
            />
        );
    };

    const syncTags = React.useCallback((orgTags: OrgTag[]) => {
        const draftTags = sortDraftTags(orgTags.map(toDraftTag));
        setTags(draftTags);
        setSavedTags(draftTags);
    }, []);

    React.useEffect(() => {
        if (!authCredentials) return;

        setIsFetchingTags(true);
        API.getOrgTags(orgId, authCredentials)
            .then(syncTags)
            .catch((error) => globalMessage.error(error.message))
            .finally(() => setIsFetchingTags(false));
    }, [authCredentials, orgId, syncTags]);

    React.useEffect(() => {
        if (!orgDestinationsData && !isLoadingOrgDestinations) {
            dispatch(tryFetchEntity(FetchableEntityType.OrgDestinationsAndVersions, { orgId }, orgId));
        }
    }, [dispatch, orgDestinationsData, isLoadingOrgDestinations, orgId]);

    React.useEffect(() => {
        if (org?.tags) {
            syncTags(org.tags);
        }
    }, [org?.tags, syncTags]);

    React.useEffect(() => {
        if (createOrgTagResponseType === RunwayResponseType.Success) {
            globalMessage.success('Tag created successfully.');
        }
    }, [createOrgTagResponseType]);

    React.useEffect(() => {
        if (updateOrgTagResponseType === RunwayResponseType.Success) {
            globalMessage.success('Tag updated successfully.');
        }
    }, [updateOrgTagResponseType]);

    React.useEffect(() => {
        if (deleteOrgTagResponseType === RunwayResponseType.Success) {
            globalMessage.success('Tag deleted successfully.');
        }
    }, [deleteOrgTagResponseType]);

    React.useEffect(() => {
        if (createOrgTagError) {
            globalMessage.error(createOrgTagError);
        }
    }, [createOrgTagError]);

    React.useEffect(() => {
        if (updateOrgTagError) {
            globalMessage.error(updateOrgTagError);
        }
    }, [updateOrgTagError]);

    React.useEffect(() => {
        if (deleteOrgTagError) {
            globalMessage.error(deleteOrgTagError);
        }
    }, [deleteOrgTagError]);

    const updateTag = (tagIndex: number, update: Partial<OrgTagDraft>) => {
        setTags((currentTags) =>
            currentTags.map((tag, currentIndex) => (currentIndex === tagIndex ? { ...tag, ...update } : tag)),
        );
    };

    const addNewTag = () => {
        setTags((currentTags) => [
            {
                tagName: '',
                color: DEFAULT_TAG_COLOR,
                destinations: [],
            },
            ...currentTags,
        ]);
    };

    const deleteTag = (tagIndex: number, tag: OrgTagDraft) => {
        if (!tag.id) {
            setTags((currentTags) => currentTags.filter((_, currentIndex) => currentIndex !== tagIndex));
            return;
        }

        deleteOrgTag({ tagId: tag.id });
    };

    const applyTag = (tag: OrgTagDraft) => {
        if (!hasRequiredTagFields(tag)) return;

        if (!tag.id) {
            createOrgTag(tagPayload(tag));
            return;
        }

        updateOrgTag({
            ...tagPayload(tag),
            tagId: tag.id,
        } satisfies UpdateOrgTagRequest);
    };

    const isDirty = (tag: OrgTagDraft) => {
        if (!tag.id) return true;

        const savedTag = savedTags.find((currentTag) => currentTag.id === tag.id);

        if (!savedTag) return true;

        return (
            savedTag.tagName !== tag.tagName ||
            savedTag.color !== tag.color ||
            savedTag.destinations.join(',') !== tag.destinations.join(',')
        );
    };

    const isMutating = createOrgTagIsLoading || updateOrgTagIsLoading || deleteOrgTagIsLoading;
    const showEmptyState = tags.length === 0 && !isFetchingTags;

    return (
        <Content>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 12 }}>
                {!showEmptyState && (
                    <Button
                        icon={<SortAscendingOutlined />}
                        style={{ alignSelf: 'flex-start' }}
                        onClick={addNewTag}
                        disabled={isMutating}
                    >
                        Create a new tag
                    </Button>
                )}

                <Spin spinning={isFetchingTags}>
                    <Space direction="vertical" size={24} style={{ width: '100%' }}>
                        {showEmptyState ?
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No tags found">
                                <Button icon={<PlusOutlined />} onClick={addNewTag} disabled={isMutating}>
                                    Create a new tag
                                </Button>
                            </Empty>
                        :   tags.map((tag, tagIndex) => {
                                const canApply = isDirty(tag) && hasRequiredTagFields(tag);

                                return (
                                    <div key={tag.id ?? `new-tag-${tagIndex}`} style={tagRowStyle}>
                                        <Form component={false} layout="horizontal" requiredMark>
                                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                                <div style={fieldRowStyle}>
                                                    <Form.Item
                                                        label="Tag name"
                                                        required
                                                        labelCol={formItemLabelCol}
                                                        colon={false}
                                                        style={formItemStyle}
                                                    >
                                                        <Input
                                                            value={tag.tagName}
                                                            onChange={(event) =>
                                                                updateTag(tagIndex, { tagName: event.target.value })
                                                            }
                                                            style={{ width: 360 }}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item
                                                        label="Color"
                                                        required
                                                        labelCol={{ flex: '60px' }}
                                                        colon={false}
                                                        style={colorFormItemStyle}
                                                    >
                                                        <ColorPicker
                                                            aria-label={`${tag.tagName || 'Tag'} color`}
                                                            value={colorPickerValue(tag.color)}
                                                            presets={tagColorPresets}
                                                            disabledAlpha
                                                            size="large"
                                                            onChange={(color) =>
                                                                updateTag(tagIndex, { color: color.toHexString() })
                                                            }
                                                        />
                                                    </Form.Item>
                                                </div>

                                                <div style={fieldRowStyle}>
                                                    <Form.Item
                                                        label="Destinations"
                                                        required
                                                        labelCol={destinationsFormItemLabelCol}
                                                        colon={false}
                                                        style={{
                                                            ...formItemStyle,
                                                            width: destinationsFormItemWidth,
                                                        }}
                                                    >
                                                        <Select
                                                            mode="multiple"
                                                            placeholder="Select destinations"
                                                            value={tag.destinations}
                                                            onChange={(destinations) =>
                                                                updateTag(tagIndex, { destinations })
                                                            }
                                                            loading={isLoadingOrgDestinations}
                                                            optionLabelProp="label"
                                                            tagRender={destinationTagRender}
                                                            style={{ width: destinationsSelectWidth }}
                                                            options={destinationOptions.map(
                                                                ({ destination, label, value }) => ({
                                                                    value,
                                                                    label,
                                                                    searchText: `${label} ${destination.appName ?? ''}`,
                                                                }),
                                                            )}
                                                            optionFilterProp="searchText"
                                                            optionRender={(option) => {
                                                                const destination =
                                                                    destinationById[String(option.value)];

                                                                return (
                                                                    <Space size={8}>
                                                                        {destination && (
                                                                            <DestinationIcon
                                                                                destination={destination}
                                                                                size={16}
                                                                            />
                                                                        )}
                                                                        <span>{option.label}</span>
                                                                    </Space>
                                                                );
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </div>

                                                <div style={actionsRowStyle}>
                                                    <Space>
                                                        <Button
                                                            icon={<DeleteOutlined />}
                                                            loading={deleteOrgTagIsLoading && !!tag.id}
                                                            disabled={isMutating}
                                                            onClick={() => deleteTag(tagIndex, tag)}
                                                        >
                                                            Delete tag
                                                        </Button>
                                                        <Button
                                                            icon={<CheckOutlined />}
                                                            disabled={!canApply || isMutating}
                                                            loading={
                                                                (!tag.id && createOrgTagIsLoading) ||
                                                                (!!tag.id && updateOrgTagIsLoading)
                                                            }
                                                            onClick={() => applyTag(tag)}
                                                        >
                                                            Apply
                                                        </Button>
                                                    </Space>
                                                </div>
                                            </Space>
                                        </Form>
                                    </div>
                                );
                            })
                        }
                    </Space>
                </Spin>
            </div>
        </Content>
    );
};

export default OrgTagsSettings;
